import React from 'react';
import { useTranslation } from 'react-i18next';

import { ClearIcon, IconSizes } from 'libs/shared/ui';

import { styled } from '@mui/material';

import { selectTagColorMode, selectTagStyling, selectTagVariant } from 'Config/configSlice';
import { CourseChip, getTagColor } from 'Courses/components/shared';
import { useAppSelector } from 'Store';
import { Colors } from 'Themes/colors';

import { AddIcon, RemoveIcon } from '@libs/shared/ui/icons';

import type { TagTypeType } from '../types';
import { TagType } from '../types';

const SearchTagChip = styled(CourseChip)<{
  tagType: TagTypeType;
}>`
  position: relative;
  cursor: pointer;
  pointer-events: auto;
  margin: 1px 2px;
  font-size: 11px;
  height: 24px;
  padding: 3px 6px 3px 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TagSymbol = styled('span')<{ tagType: TagTypeType; tagColor: string }>`
  margin-right: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${({ tagType }) => (tagType === TagType.Include ? Colors.green : Colors.red)};
  border: 1px solid ${({ tagColor }) => tagColor};
  color: ${Colors.white};

  svg {
    font-size: 10px;
    color: ${Colors.white};
  }
`;

const DeleteButton = styled('button')<{ tagColor: string }>`
  background: none;
  border: none;
  color: ${Colors.darkGrey};
  margin-left: 8px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  min-width: 16px;
  min-height: 16px;

  &:hover {
    color: ${Colors.red};
    opacity: 0.9;
  }

  &:focus {
    outline: 2px solid ${Colors.blue};
    outline-offset: 2px;
    border-radius: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

type SearchTagProps = Readonly<{
  name: string;
  type: TagTypeType;
  displayName: string;
  onToggle: () => void;
  onRemove: (tagName: string) => void;
  disabled?: boolean;
}>;

export const SearchTag = ({
  name,
  type,
  displayName,
  onToggle,
  onRemove,
  disabled = false
}: SearchTagProps) => {
  const { t } = useTranslation();

  const safeName = name || displayName || 'unknown';
  const tagVariant = useAppSelector(selectTagVariant);
  const tagColorMode = useAppSelector(selectTagColorMode);
  const tagStyling = useAppSelector(selectTagStyling);
  const tagColor = getTagColor(safeName, tagVariant, tagStyling.uniformColor, tagColorMode);

  const handleToggle = () => {
    if (!disabled) {
      onToggle();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      onRemove(name);
    }
  };

  const SearchTagChipElement = SearchTagChip as any;

  return (
    <SearchTagChipElement
      backgroundColor={tagColor}
      variant={tagVariant}
      uniformColor={tagStyling.uniformColor}
      borderSize={tagStyling.borderSize}
      fontSize={tagStyling.fontSize}
      colorMode={tagColorMode}
      tagType={type}
      onClick={handleToggle}
    >
      <TagSymbol tagType={type} tagColor={tagColor}>
        {type === TagType.Include ? (
          <AddIcon fontSize="inherit" />
        ) : (
          <RemoveIcon fontSize="inherit" />
        )}
      </TagSymbol>
      {displayName}
      <DeleteButton
        onClick={handleRemove}
        aria-label={t('coursesPage.removeTag', 'Remove tag')}
        disabled={disabled}
        type="button"
        tagColor={tagColor}
      >
        <ClearIcon fontSize={IconSizes.small} />
      </DeleteButton>
    </SearchTagChipElement>
  );
};
